import { ClassValue } from 'clsx';
import { Text } from '@shared/components/Text';
import { Pill } from '@shared/components/pill';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getNumberText } from './util/getNumberText';
import styles from './NumberCollectionViewField.module.css';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: NumberField;
}

/**
 * Default: the number in a pill. Compact: plain text with tabular figures, so digits
 * line up down a column. Inline: as text.
 */
export function NumberCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.numberCollectionViewField, className]}
    >
      {isInline && <InlineNumber field={field} />}
      {isCompact && <CompactNumber field={field} />}
      {isDefault && <DefaultNumber field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineNumber({ field }: { field: NumberField }) {
  return <>{getNumberText(field)}</>;
}

function CompactNumber({ field }: { field: NumberField }) {
  return <Text numberOfLines={1}>{getNumberText(field)}</Text>;
}

function DefaultNumber({ field }: { field: NumberField }) {
  return <Pill sections={[field.formattedValue]} />;
}
